import React, { useMemo, useState } from 'react';
import { AlertTriangle, Building2, FlaskConical, Loader2, PencilLine, Plus, Save, Search, Trash2, Users, Wrench, X } from 'lucide-react';
import type {
  ArbeitsbereichEntwurf, ArbeitsmittelEntwurf, ArbeitsmittelGestelltVon, Kataster, Signalwort, SifaArbeitsbereich, SifaArbeitsmittel, SifaGbu,
  SifaMitarbeiterBereich, SifaStoff, StoffArt, StoffEntwurf,
} from '../../../services/sifa/gbuTypes';
import type { SifaMitarbeiter } from '../../../services/sifa/types';
import {
  loescheArbeitsbereich, loescheArbeitsmittel, loescheStoff, setzeBereichsMitarbeiter, speichereArbeitsbereich, speichereArbeitsmittel, speichereStoff,
} from '../../../services/sifa/katasterService';
import { rechtFehlerOder } from '../../../services/rechte/rechtFehler';
import { RueckfrageDialog } from '../vorlagen/RueckfrageDialog';
import {
  GESTELLT_VON,
  GESTELLT_VON_LABEL,
  GHS_CODES,
  GHS_LABEL,
  SIGNALWORT_LABEL,
  STOFF_ART_LABEL,
  arbeitsmittelEntwurfAus,
  bereichEntwurfAus,
  bereichLoeschSperre,
  bereinigeArbeitsmittel,
  bereinigeBereich,
  bereinigeStoff,
  laufendeMitarbeiterIds,
  leererBereich,
  leererStoff,
  leeresArbeitsmittel,
  mitarbeiterJeBereich,
  parseHSaetze,
  parseZahl,
  pruefHinweis,
  pruefeArbeitsmittel,
  pruefeBereich,
  pruefeStoff,
  schalteId,
  sortiereKataster,
  stoffEntwurfAus,
  sucheMitarbeiter,
  unlesbareHSaetze,
  waehlbareMitarbeiter,
  zahlText,
  formatTag,
} from './gbuModulLogic';

type Reiter = 'bereiche' | 'arbeitsmittel' | 'stoffe';

interface Props {
  projectId: string;
  kataster: Kataster;
  mitarbeiter: SifaMitarbeiter[];
  gbus: SifaGbu[];
  companyName: string | null;
  heute: string;
  isReadOnly: boolean;
  isMobile: boolean;
  onKataster: (aendere: (k: Kataster) => Kataster) => void;
  onZuordnungen: (bereichId: string, zuordnungen: SifaMitarbeiterBereich[]) => void;
}

type Loeschen =
  | { art: 'bereich'; eintrag: SifaArbeitsbereich }
  | { art: 'arbeitsmittel'; eintrag: SifaArbeitsmittel }
  | { art: 'stoff'; eintrag: SifaStoff };

const FELD = 'w-full px-3 py-2 min-h-[44px] text-sm text-slate-800 bg-slate-50 rounded-lg outline-none focus:bg-white focus:ring-1 focus:ring-amber-300 disabled:text-slate-500';
const LABEL = 'block text-[11px] font-bold uppercase text-slate-500 mb-1';

/** Ersetzt bzw. ergaenzt einen Eintrag einer Kataster-Liste. */
const ersetze = <T extends { id: string }>(liste: T[], e: T): T[] =>
  liste.some((x) => x.id === e.id) ? liste.map((x) => (x.id === e.id ? e : x)) : [...liste, e];

/**
 * „Betrieb" — das Betriebskataster des SiFa-Projekts (Plan §4.2): einmal
 * gepflegt, von allen Kacheln benutzt.
 *
 * - Arbeitsbereiche/Taetigkeiten mit den Personen, die dort arbeiten
 *   (Mitarbeiterstamm des Projekts, Zuordnung mit seit/bis — E2),
 * - Arbeitsmittel (wer stellt und prueft: eigen, Auftraggeber, Verleiher),
 * - Gefahr- und Biostoffe (Signalwort, H-Saetze, GHS, Menge, SDB-Datum).
 *
 * Geloescht wird nur, was keine GBU benutzt — sonst „inaktiv". Die Absage
 * kommt als Klartext aus katasterService.
 */
export const BetriebAnsicht: React.FC<Props> = ({ projectId, kataster, mitarbeiter, gbus, companyName, heute, isReadOnly, isMobile, onKataster, onZuordnungen }) => {
  const [reiter, setReiter] = useState<Reiter>('bereiche');
  const [bereichDialog, setBereichDialog] = useState<{ eintrag: SifaArbeitsbereich | null } | null>(null);
  const [mittelDialog, setMittelDialog] = useState<{ eintrag: SifaArbeitsmittel | null } | null>(null);
  const [stoffDialog, setStoffDialog] = useState<{ eintrag: SifaStoff | null } | null>(null);
  const [loeschen, setLoeschen] = useState<Loeschen | null>(null);
  const [loeschtLaeuft, setLoeschtLaeuft] = useState(false);
  const [meldung, setMeldung] = useState<{ text: string; fehler: boolean } | null>(null);

  const zaehlerMa = useMemo(() => mitarbeiterJeBereich(kataster.zuordnungen), [kataster.zuordnungen]);
  const bereiche = useMemo(() => sortiereKataster(kataster.bereiche), [kataster.bereiche]);
  const mittel = useMemo(() => sortiereKataster(kataster.arbeitsmittel), [kataster.arbeitsmittel]);
  const stoffe = useMemo(() => sortiereKataster(kataster.stoffe), [kataster.stoffe]);

  const loeschenBestaetigt = async () => {
    if (!loeschen) return;
    setLoeschtLaeuft(true);
    try {
      if (loeschen.art === 'bereich') {
        await loescheArbeitsbereich(loeschen.eintrag.id);
        const id = loeschen.eintrag.id;
        onKataster((k) => ({ ...k, bereiche: k.bereiche.filter((b) => b.id !== id), zuordnungen: k.zuordnungen.filter((z) => z.bereichId !== id) }));
      } else if (loeschen.art === 'arbeitsmittel') {
        await loescheArbeitsmittel(loeschen.eintrag.id);
        const id = loeschen.eintrag.id;
        onKataster((k) => ({ ...k, arbeitsmittel: k.arbeitsmittel.filter((a) => a.id !== id) }));
      } else {
        await loescheStoff(loeschen.eintrag.id);
        const id = loeschen.eintrag.id;
        onKataster((k) => ({ ...k, stoffe: k.stoffe.filter((s) => s.id !== id) }));
      }
      setMeldung({ text: `„${loeschen.eintrag.bezeichnung}" gelöscht.`, fehler: false });
    } catch (e) {
      setMeldung({ text: rechtFehlerOder(e), fehler: true });
    } finally {
      setLoeschtLaeuft(false);
      setLoeschen(null);
    }
  };

  const reiterKnopf = (r: Reiter, label: string, Icon: typeof Building2, anzahl: number) => (
    <button key={r} onClick={() => setReiter(r)} className={`min-h-[40px] px-3 flex items-center justify-center gap-1.5 text-xs font-bold rounded-lg ${reiter === r ? 'bg-white shadow text-slate-800' : 'text-slate-500'}`}>
      <Icon className="w-4 h-4" /> {label} <span className="text-slate-400">({anzahl})</span>
    </button>
  );

  const neu = () => {
    if (isReadOnly) return;
    setMeldung(null);
    if (reiter === 'bereiche') setBereichDialog({ eintrag: null });
    else if (reiter === 'arbeitsmittel') setMittelDialog({ eintrag: null });
    else setStoffDialog({ eintrag: null });
  };

  const inaktivChip = <span className="px-1.5 py-px text-[10px] font-bold text-slate-500 bg-slate-100 border border-slate-200 rounded-full">inaktiv</span>;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-lg font-bold text-slate-800">Betrieb</h2>
          <p className="text-xs text-slate-500">Arbeitsbereiche, Arbeitsmittel und Stoffe — Grundlage aller Gefährdungsbeurteilungen.</p>
        </div>
        {!isReadOnly && (
          <button onClick={neu} className="min-h-[44px] px-4 flex items-center gap-2 text-sm font-bold text-white bg-amber-600 hover:bg-amber-700 rounded-xl">
            <Plus className="w-4 h-4" /> {reiter === 'bereiche' ? 'Arbeitsbereich' : reiter === 'arbeitsmittel' ? 'Arbeitsmittel' : 'Stoff'}
          </button>
        )}
      </div>

      <div className={`grid ${isMobile ? 'grid-cols-1' : 'grid-cols-3'} gap-1 p-1 bg-slate-100 rounded-xl`}>
        {reiterKnopf('bereiche', 'Arbeitsbereiche', Building2, kataster.bereiche.length)}
        {reiterKnopf('arbeitsmittel', 'Arbeitsmittel', Wrench, kataster.arbeitsmittel.length)}
        {reiterKnopf('stoffe', 'Gefahr- und Biostoffe', FlaskConical, kataster.stoffe.length)}
      </div>

      {meldung && (
        <div className={`p-3 rounded-xl text-xs flex items-start gap-2 border ${meldung.fehler ? 'bg-red-50 border-red-200 text-red-700' : 'bg-blue-50 border-blue-200 text-blue-800'}`}>
          {meldung.fehler && <AlertTriangle className="w-4 h-4 shrink-0 mt-px" />}
          <span className="flex-1">{meldung.text}</span>
          <button onClick={() => setMeldung(null)} className="p-1"><X className="w-3.5 h-3.5" /></button>
        </div>
      )}

      {reiter === 'bereiche' && (
        bereiche.length === 0 ? (
          <Leer text={'Noch kein Arbeitsbereich. Ein Arbeitsbereich ist eine Tätigkeit oder ein Ort mit eigener Gefährdungsbeurteilung, z. B. „Einschalen", „Lager" oder „Büro".'} />
        ) : (
          <div className={`grid gap-3 ${isMobile ? 'grid-cols-1' : 'grid-cols-1 lg:grid-cols-2'}`}>
            {bereiche.map((b) => {
              const anzahlGbu = gbus.filter((g) => g.bereichId === b.id && g.status !== 'archiviert').length;
              return (
                <div key={b.id} className={`bg-white border border-slate-200 rounded-2xl p-4 space-y-2 ${b.aktiv ? '' : 'opacity-70'}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-slate-800">{b.bezeichnung} {!b.aktiv && inaktivChip}</p>
                      <p className="text-xs text-slate-500">{[b.bereich, b.ort].filter(Boolean).join(' · ') || '—'}</p>
                    </div>
                    <EintragKnoepfe isReadOnly={isReadOnly} onBearbeiten={() => setBereichDialog({ eintrag: b })} onLoeschen={() => {
                      // Eine GBU haelt den Bereich (Fremdschluessel restrict): gar nicht erst fragen.
                      const sperre = bereichLoeschSperre(b.id, gbus);
                      if (sperre) setMeldung({ text: sperre, fehler: true });
                      else setLoeschen({ art: 'bereich', eintrag: b });
                    }} />
                  </div>
                  {b.beschreibung && <p className="text-xs text-slate-600 line-clamp-3 whitespace-pre-line">{b.beschreibung}</p>}
                  <p className="text-[11px] text-slate-500 flex items-center gap-3">
                    <span className="inline-flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {zaehlerMa[b.id] ?? 0} Mitarbeiter</span>
                    <span>{anzahlGbu} GBU</span>
                  </p>
                </div>
              );
            })}
          </div>
        )
      )}

      {reiter === 'arbeitsmittel' && (
        mittel.length === 0 ? (
          <Leer text="Noch kein Arbeitsmittel. Erfasst werden Maschinen, Geräte und Werkzeuge — mit der Angabe, wer sie stellt und prüft." />
        ) : (
          <div className="bg-white border border-slate-200 rounded-2xl divide-y divide-slate-100">
            {mittel.map((a) => (
              <div key={a.id} className={`p-3 flex items-start gap-3 ${a.aktiv ? '' : 'opacity-70'}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-slate-800">{a.bezeichnung} {!a.aktiv && inaktivChip}</p>
                  <p className="text-xs text-slate-500">{[a.typ, a.hersteller].filter(Boolean).join(' · ') || '—'} · {GESTELLT_VON_LABEL[a.gestelltVon]}</p>
                  {pruefHinweis(a) && <p className="text-[11px] text-slate-500">{pruefHinweis(a)}{a.pruefer ? ` · Prüfer: ${a.pruefer}` : ''}</p>}
                </div>
                <EintragKnoepfe isReadOnly={isReadOnly} onBearbeiten={() => setMittelDialog({ eintrag: a })} onLoeschen={() => setLoeschen({ art: 'arbeitsmittel', eintrag: a })} />
              </div>
            ))}
          </div>
        )
      )}

      {reiter === 'stoffe' && (
        stoffe.length === 0 ? (
          <Leer text="Noch kein Stoff. Gefahrstoffe mit Signalwort, H-Sätzen und GHS-Piktogrammen bilden später das Gefahrstoffverzeichnis; Biostoffe mit Risikogruppe." />
        ) : (
          <div className="bg-white border border-slate-200 rounded-2xl divide-y divide-slate-100">
            {stoffe.map((s) => (
              <div key={s.id} className={`p-3 flex items-start gap-3 ${s.aktiv ? '' : 'opacity-70'}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-slate-800">
                    {s.bezeichnung} {!s.aktiv && inaktivChip}
                    {s.signalwort && <span className={`ml-1 px-1.5 py-px text-[10px] font-black rounded ${s.signalwort === 'gefahr' ? 'text-white bg-red-600' : 'text-amber-900 bg-amber-200'}`}>{SIGNALWORT_LABEL[s.signalwort].toUpperCase()}</span>}
                  </p>
                  <p className="text-xs text-slate-500">
                    {STOFF_ART_LABEL[s.art]}{s.hersteller ? ` · ${s.hersteller}` : ''}
                    {s.art === 'biostoff' && s.risikogruppe ? ` · Risikogruppe ${s.risikogruppe}` : ''}
                    {s.mengeJahr ? ` · ${s.mengeJahr}/Jahr` : ''}
                  </p>
                  {(s.hSaetze.length > 0 || s.ghs.length > 0) && <p className="text-[11px] text-slate-500">{[...s.ghs, ...s.hSaetze].join(' · ')}</p>}
                  {s.art === 'gefahrstoff' && (
                    <p className={`text-[11px] ${s.sdbDatum ? 'text-slate-500' : 'text-amber-700'}`}>
                      {s.sdbDatum ? `SDB vom ${formatTag(s.sdbDatum)}` : 'Sicherheitsdatenblatt: Datum fehlt'}
                    </p>
                  )}
                </div>
                <EintragKnoepfe isReadOnly={isReadOnly} onBearbeiten={() => setStoffDialog({ eintrag: s })} onLoeschen={() => setLoeschen({ art: 'stoff', eintrag: s })} />
              </div>
            ))}
          </div>
        )
      )}

      {bereichDialog && (
        <BereichDialog
          projectId={projectId}
          nurLesen={isReadOnly}
          eintrag={bereichDialog.eintrag}
          mitarbeiter={mitarbeiter}
          zuordnungen={kataster.zuordnungen}
          companyName={companyName}
          heute={heute}
          onClose={() => setBereichDialog(null)}
          onGespeichert={(b, zuordnungen, text) => {
            onKataster((k) => ({ ...k, bereiche: ersetze(k.bereiche, b) }));
            if (zuordnungen) onZuordnungen(b.id, zuordnungen);
            setBereichDialog(null);
            setMeldung(text ? { text, fehler: true } : { text: `„${b.bezeichnung}" gespeichert.`, fehler: false });
          }}
        />
      )}
      {mittelDialog && (
        <ArbeitsmittelDialog
          projectId={projectId}
          nurLesen={isReadOnly}
          eintrag={mittelDialog.eintrag}
          companyName={companyName}
          onClose={() => setMittelDialog(null)}
          onGespeichert={(a) => {
            onKataster((k) => ({ ...k, arbeitsmittel: ersetze(k.arbeitsmittel, a) }));
            setMittelDialog(null);
            setMeldung({ text: `„${a.bezeichnung}" gespeichert.`, fehler: false });
          }}
        />
      )}
      {stoffDialog && (
        <StoffDialog
          projectId={projectId}
          nurLesen={isReadOnly}
          eintrag={stoffDialog.eintrag}
          companyName={companyName}
          onClose={() => setStoffDialog(null)}
          onGespeichert={(s) => {
            onKataster((k) => ({ ...k, stoffe: ersetze(k.stoffe, s) }));
            setStoffDialog(null);
            setMeldung({ text: `„${s.bezeichnung}" gespeichert.`, fehler: false });
          }}
        />
      )}

      {loeschen && (
        <RueckfrageDialog
          titel={`„${loeschen.eintrag.bezeichnung}" löschen`}
          text={`${loeschen.art === 'bereich' ? 'Den Arbeitsbereich samt Mitarbeiter-Zuordnungen' : loeschen.art === 'arbeitsmittel' ? 'Das Arbeitsmittel' : 'Den Stoff'} löschen? Wird er in einer Gefährdungsbeurteilung genannt, lehnt die Datenbank ab — dann auf „inaktiv" setzen.`}
          jaText="Löschen"
          gefaehrlich
          laeuft={loeschtLaeuft}
          onNein={() => setLoeschen(null)}
          onJa={() => void loeschenBestaetigt()}
        />
      )}
    </div>
  );
};

const Leer: React.FC<{ text: string }> = ({ text }) => (
  <div className="p-6 bg-white border border-dashed border-slate-300 rounded-2xl text-center text-xs text-slate-500">{text}</div>
);

const EintragKnoepfe: React.FC<{ isReadOnly: boolean; onBearbeiten: () => void; onLoeschen: () => void }> = ({ isReadOnly, onBearbeiten, onLoeschen }) => (
  <div className="flex gap-1 shrink-0">
    <button onClick={onBearbeiten} title={isReadOnly ? 'Ansehen' : 'Bearbeiten'} className="p-2 min-h-[40px] min-w-[40px] flex items-center justify-center text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-lg">
      <PencilLine className="w-4 h-4" />
    </button>
    {!isReadOnly && (
      <button onClick={onLoeschen} title="Löschen" className="p-2 min-h-[40px] min-w-[40px] flex items-center justify-center text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg">
        <Trash2 className="w-4 h-4" />
      </button>
    )}
  </div>
);

/** Rahmen der drei Pflegedialoge. */
const Dialog: React.FC<{ titel: string; laeuft: boolean; fehler: string[]; nurLesen: boolean; onClose: () => void; onSpeichern: () => void; children: React.ReactNode }> = ({ titel, laeuft, fehler, nurLesen, onClose, onSpeichern, children }) => (
  <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-3 sm:p-4" onClick={laeuft ? undefined : onClose}>
    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[95vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
      <div className="p-4 border-b border-slate-200 flex items-center justify-between shrink-0">
        <h3 className="text-sm font-bold text-slate-800">{titel}</h3>
        <button onClick={onClose} disabled={laeuft} className="p-2 hover:bg-slate-100 rounded-full disabled:opacity-40"><X className="w-4 h-4 text-slate-500" /></button>
      </div>
      <div className="p-4 space-y-3 overflow-y-auto">
        <fieldset disabled={nurLesen} className="space-y-3 min-w-0">{children}</fieldset>
        {fehler.length > 0 && <ul className="text-xs text-red-700 space-y-0.5">{fehler.map((f) => <li key={f}>{f}</li>)}</ul>}
      </div>
      <div className="p-4 border-t border-slate-200 flex justify-end gap-2 shrink-0">
        <button onClick={onClose} disabled={laeuft} className="min-h-[44px] px-4 text-sm font-semibold text-slate-600 hover:bg-slate-100 rounded-xl disabled:opacity-40">{nurLesen ? 'Schließen' : 'Abbrechen'}</button>
        {!nurLesen && (
          <button onClick={onSpeichern} disabled={laeuft} className="min-h-[44px] px-4 flex items-center gap-2 text-sm font-bold text-white bg-amber-600 hover:bg-amber-700 rounded-xl disabled:opacity-40">
            {laeuft ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Speichern
          </button>
        )}
      </div>
    </div>
  </div>
);

// ─── Arbeitsbereich ─────────────────────────────────────────────────────────

const BereichDialog: React.FC<{
  projectId: string;
  nurLesen: boolean;
  eintrag: SifaArbeitsbereich | null;
  mitarbeiter: SifaMitarbeiter[];
  zuordnungen: SifaMitarbeiterBereich[];
  companyName: string | null;
  heute: string;
  onClose: () => void;
  /** `text`: Bereich gespeichert, aber die Zuordnung scheiterte (Klartext). */
  onGespeichert: (b: SifaArbeitsbereich, zuordnungen: SifaMitarbeiterBereich[] | null, text: string | null) => void;
}> = ({ projectId, nurLesen, eintrag, mitarbeiter, zuordnungen, companyName, heute, onClose, onGespeichert }) => {
  const [e, setE] = useState<ArbeitsbereichEntwurf>(() => (eintrag ? bereichEntwurfAus(eintrag) : leererBereich()));
  const vorher = useMemo(() => (eintrag ? laufendeMitarbeiterIds(zuordnungen, eintrag.id) : []), [eintrag, zuordnungen]);
  const [gewaehlt, setGewaehlt] = useState<string[]>(vorher);
  const [suche, setSuche] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string[]>([]);
  const setze = <K extends keyof ArbeitsbereichEntwurf>(k: K, v: ArbeitsbereichEntwurf[K]) => setE((p) => ({ ...p, [k]: v }));
  const auswahl = sucheMitarbeiter(waehlbareMitarbeiter(mitarbeiter, gewaehlt), suche);
  const zuordnungGeaendert = gewaehlt.length !== vorher.length || gewaehlt.some((id) => !vorher.includes(id));

  const speichern = async () => {
    const probleme = pruefeBereich(e);
    if (probleme.length) { setFehler(probleme); return; }
    setLaeuft(true);
    setFehler([]);
    try {
      const b = await speichereArbeitsbereich(projectId, bereinigeBereich(e), eintrag?.id, companyName);
      if (!zuordnungGeaendert) { onGespeichert(b, null, null); return; }
      try {
        const neu = await setzeBereichsMitarbeiter(projectId, b.id, gewaehlt, heute);
        onGespeichert(b, neu, null);
      } catch (err) {
        onGespeichert(b, null, `„${b.bezeichnung}" gespeichert, die Mitarbeiter-Zuordnung aber nicht: ${rechtFehlerOder(err)}`);
      }
    } catch (err) {
      setFehler([rechtFehlerOder(err)]);
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <Dialog titel={eintrag ? 'Arbeitsbereich bearbeiten' : 'Neuer Arbeitsbereich'} laeuft={laeuft} fehler={fehler} nurLesen={nurLesen} onClose={onClose} onSpeichern={() => void speichern()}>
      <label className="block">
        <span className={LABEL}>Bezeichnung (Tätigkeit)</span>
        <input value={e.bezeichnung} onChange={(ev) => setze('bezeichnung', ev.target.value)} className={FELD} placeholder="z. B. Einschalen" autoFocus={!eintrag} />
      </label>
      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <span className={LABEL}>Bereich</span>
          <input value={e.bereich ?? ''} onChange={(ev) => setze('bereich', ev.target.value)} className={FELD} placeholder="z. B. Rohbau" />
        </label>
        <label className="block">
          <span className={LABEL}>Ort</span>
          <input value={e.ort ?? ''} onChange={(ev) => setze('ort', ev.target.value)} className={FELD} placeholder="z. B. Baustellen" />
        </label>
      </div>
      <label className="block">
        <span className={LABEL}>Beschreibung der Tätigkeit</span>
        <textarea value={e.beschreibung ?? ''} onChange={(ev) => setze('beschreibung', ev.target.value)} rows={3} className={`${FELD} min-h-[80px]`} placeholder="Was wird getan, womit, wo? Grundlage des KI-Vorschlags — keine Namen von Personen." />
      </label>
      <label className="flex items-center gap-2 text-sm text-slate-700">
        <input type="checkbox" checked={e.aktiv} onChange={(ev) => setze('aktiv', ev.target.checked)} /> aktiv
      </label>

      <div className="space-y-2 border-t border-slate-100 pt-3">
        <p className={LABEL}>Mitarbeiter in diesem Bereich ({gewaehlt.length})</p>
        {mitarbeiter.length === 0 ? (
          <p className="text-xs text-slate-500">Kein Mitarbeiterstamm — Mitarbeiter werden im Modul „Mitarbeiter" angelegt.</p>
        ) : (
          <>
            <div className="relative">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input value={suche} onChange={(ev) => setSuche(ev.target.value)} placeholder="Suchen" className={`${FELD} pl-9`} />
            </div>
            <div className="max-h-56 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
              {auswahl.map((m) => (
                <label key={m.id} className="flex items-center gap-2 px-3 py-2 min-h-[44px] text-sm text-slate-700 cursor-pointer hover:bg-slate-50">
                  <input type="checkbox" checked={gewaehlt.includes(m.id)} onChange={() => setGewaehlt((g) => schalteId(g, m.id))} />
                  <span className="flex-1 min-w-0 truncate">{m.nachname}, {m.vorname}</span>
                  {m.abteilung && <span className="text-[11px] text-slate-400 truncate">{m.abteilung}</span>}
                  {m.aktiv === false && <span className="text-[10px] text-slate-400">inaktiv</span>}
                </label>
              ))}
              {auswahl.length === 0 && <p className="px-3 py-2 text-xs text-slate-400">Kein Treffer.</p>}
            </div>
            <p className="text-[11px] text-slate-500">Neu angehakte gelten ab heute; abgewählte werden mit dem heutigen Tag beendet (die Historie bleibt für die Vorsorge).</p>
          </>
        )}
      </div>
    </Dialog>
  );
};

// ─── Arbeitsmittel ──────────────────────────────────────────────────────────

const ArbeitsmittelDialog: React.FC<{
  projectId: string;
  nurLesen: boolean;
  eintrag: SifaArbeitsmittel | null;
  companyName: string | null;
  onClose: () => void;
  onGespeichert: (a: SifaArbeitsmittel) => void;
}> = ({ projectId, nurLesen, eintrag, companyName, onClose, onGespeichert }) => {
  const [e, setE] = useState<ArbeitsmittelEntwurf>(() => (eintrag ? arbeitsmittelEntwurfAus(eintrag) : leeresArbeitsmittel()));
  const [intervall, setIntervall] = useState(() => zahlText(eintrag?.pruefIntervallMonate ?? null));
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string[]>([]);
  const setze = <K extends keyof ArbeitsmittelEntwurf>(k: K, v: ArbeitsmittelEntwurf[K]) => setE((p) => ({ ...p, [k]: v }));

  const speichern = async () => {
    const n = intervall.trim() ? parseZahl(intervall) : null;
    if (intervall.trim() && (n === null || !Number.isInteger(n))) { setFehler(['Das Prüfintervall ist eine ganze Zahl von Monaten.']); return; }
    const entwurf = { ...e, pruefIntervallMonate: n };
    const probleme = pruefeArbeitsmittel(entwurf);
    if (probleme.length) { setFehler(probleme); return; }
    setLaeuft(true);
    setFehler([]);
    try {
      onGespeichert(await speichereArbeitsmittel(projectId, bereinigeArbeitsmittel(entwurf), eintrag?.id, companyName));
    } catch (err) {
      setFehler([rechtFehlerOder(err)]);
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <Dialog titel={eintrag ? 'Arbeitsmittel bearbeiten' : 'Neues Arbeitsmittel'} laeuft={laeuft} fehler={fehler} nurLesen={nurLesen} onClose={onClose} onSpeichern={() => void speichern()}>
      <label className="block">
        <span className={LABEL}>Bezeichnung</span>
        <input value={e.bezeichnung} onChange={(ev) => setze('bezeichnung', ev.target.value)} className={FELD} placeholder="z. B. Tischkreissäge" autoFocus={!eintrag} />
      </label>
      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <span className={LABEL}>Typ</span>
          <input value={e.typ ?? ''} onChange={(ev) => setze('typ', ev.target.value)} className={FELD} />
        </label>
        <label className="block">
          <span className={LABEL}>Hersteller</span>
          <input value={e.hersteller ?? ''} onChange={(ev) => setze('hersteller', ev.target.value)} className={FELD} />
        </label>
      </div>
      <label className="block">
        <span className={LABEL}>Gestellt von</span>
        <select value={e.gestelltVon} onChange={(ev) => setze('gestelltVon', ev.target.value as ArbeitsmittelGestelltVon)} className={FELD}>
          {GESTELLT_VON.map((g) => <option key={g} value={g}>{GESTELLT_VON_LABEL[g]}</option>)}
        </select>
        {e.gestelltVon !== 'eigen' && (
          <span className="block text-[11px] text-slate-500 mt-1">
            Der {e.gestelltVon === 'auftraggeber' ? 'Auftraggeber' : 'Verleiher'} stellt und prüft — die App legt keine eigene Prüffrist an, es wird „Prüfnachweis beim {e.gestelltVon === 'auftraggeber' ? 'Auftraggeber' : 'Verleiher'}" vermerkt.
          </span>
        )}
      </label>
      <label className="block">
        <span className={LABEL}>Was ist zu prüfen</span>
        <input value={e.pruefung ?? ''} onChange={(ev) => setze('pruefung', ev.target.value)} className={FELD} placeholder="z. B. Elektrische Sicherheit (DGUV V3)" />
      </label>
      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <span className={LABEL}>Intervall (Monate)</span>
          <input value={intervall} onChange={(ev) => setIntervall(ev.target.value)} className={FELD} inputMode="numeric" placeholder="z. B. 12" />
        </label>
        <label className="block">
          <span className={LABEL}>Prüfer</span>
          <input value={e.pruefer ?? ''} onChange={(ev) => setze('pruefer', ev.target.value)} className={FELD} placeholder="z. B. Elektrofachkraft" />
        </label>
      </div>
      <label className="block">
        <span className={LABEL}>Notiz</span>
        <input value={e.notiz ?? ''} onChange={(ev) => setze('notiz', ev.target.value)} className={FELD} />
      </label>
      <label className="flex items-center gap-2 text-sm text-slate-700">
        <input type="checkbox" checked={e.aktiv} onChange={(ev) => setze('aktiv', ev.target.checked)} /> aktiv
      </label>
    </Dialog>
  );
};

// ─── Stoff ──────────────────────────────────────────────────────────────────

const StoffDialog: React.FC<{
  projectId: string;
  nurLesen: boolean;
  eintrag: SifaStoff | null;
  companyName: string | null;
  onClose: () => void;
  onGespeichert: (s: SifaStoff) => void;
}> = ({ projectId, nurLesen, eintrag, companyName, onClose, onGespeichert }) => {
  const [e, setE] = useState<StoffEntwurf>(() => (eintrag ? stoffEntwurfAus(eintrag) : leererStoff()));
  const [hText, setHText] = useState(() => (eintrag?.hSaetze ?? []).join(', '));
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string[]>([]);
  const setze = <K extends keyof StoffEntwurf>(k: K, v: StoffEntwurf[K]) => setE((p) => ({ ...p, [k]: v }));
  const unlesbar = unlesbareHSaetze(hText);

  const speichern = async () => {
    // H-Saetze gibt es nur bei Gefahrstoffen — bei Biostoffen spielt eine Resteingabe keine Rolle.
    const entwurf = { ...e, hSaetze: e.art === 'gefahrstoff' ? parseHSaetze(hText) : [] };
    const probleme = pruefeStoff(entwurf);
    if (e.art === 'gefahrstoff' && unlesbar.length) probleme.push(`Unlesbare H-Sätze: ${unlesbar.join(', ')}`);
    if (probleme.length) { setFehler(probleme); return; }
    setLaeuft(true);
    setFehler([]);
    try {
      onGespeichert(await speichereStoff(projectId, bereinigeStoff(entwurf), eintrag?.id, companyName));
    } catch (err) {
      setFehler([rechtFehlerOder(err)]);
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <Dialog titel={eintrag ? 'Stoff bearbeiten' : 'Neuer Stoff'} laeuft={laeuft} fehler={fehler} nurLesen={nurLesen} onClose={onClose} onSpeichern={() => void speichern()}>
      <div className="grid grid-cols-2 gap-1 p-1 bg-slate-100 rounded-xl">
        {(['gefahrstoff', 'biostoff'] as StoffArt[]).map((a) => (
          <button key={a} type="button" onClick={() => setze('art', a)} className={`min-h-[40px] text-sm font-bold rounded-lg ${e.art === a ? 'bg-white shadow text-slate-800' : 'text-slate-500'}`}>
            {STOFF_ART_LABEL[a]}
          </button>
        ))}
      </div>
      <label className="block">
        <span className={LABEL}>Bezeichnung</span>
        <input value={e.bezeichnung} onChange={(ev) => setze('bezeichnung', ev.target.value)} className={FELD} placeholder={e.art === 'gefahrstoff' ? 'z. B. Zement / Frischbeton' : 'z. B. Schimmelpilze'} autoFocus={!eintrag} />
      </label>
      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <span className={LABEL}>Hersteller</span>
          <input value={e.hersteller ?? ''} onChange={(ev) => setze('hersteller', ev.target.value)} className={FELD} />
        </label>
        <label className="block">
          <span className={LABEL}>CAS-Nr.</span>
          <input value={e.cas ?? ''} onChange={(ev) => setze('cas', ev.target.value)} className={FELD} />
        </label>
      </div>
      {e.art === 'gefahrstoff' ? (
        <>
          <label className="block">
            <span className={LABEL}>Signalwort</span>
            <select value={e.signalwort ?? ''} onChange={(ev) => setze('signalwort', (ev.target.value || null) as Signalwort | null)} className={FELD}>
              <option value="">— keins —</option>
              <option value="gefahr">{SIGNALWORT_LABEL.gefahr}</option>
              <option value="achtung">{SIGNALWORT_LABEL.achtung}</option>
            </select>
          </label>
          <label className="block">
            <span className={LABEL}>H-Sätze</span>
            <input value={hText} onChange={(ev) => setHText(ev.target.value)} className={FELD} placeholder="z. B. H315, H318, H317" />
            {unlesbar.length > 0 && <span className="block text-[11px] text-red-700 mt-1">Nicht erkannt: {unlesbar.join(', ')}</span>}
          </label>
          <div>
            <span className={LABEL}>GHS-Piktogramme</span>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-1">
              {GHS_CODES.map((c) => (
                <label key={c} className="flex items-center gap-2 text-xs text-slate-700 min-h-[36px]">
                  <input type="checkbox" checked={e.ghs.includes(c)} onChange={() => setze('ghs', schalteId(e.ghs, c))} />
                  <span><b>{c}</b> {GHS_LABEL[c]}</span>
                </label>
              ))}
            </div>
          </div>
        </>
      ) : (
        <label className="block">
          <span className={LABEL}>Risikogruppe</span>
          <select value={e.risikogruppe ?? ''} onChange={(ev) => setze('risikogruppe', ev.target.value ? Number(ev.target.value) : null)} className={FELD}>
            <option value="">— unbekannt —</option>
            {[1, 2, 3, 4].map((n) => <option key={n} value={n}>Risikogruppe {n}</option>)}
          </select>
        </label>
      )}
      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <span className={LABEL}>Menge je Jahr</span>
          <input value={e.mengeJahr ?? ''} onChange={(ev) => setze('mengeJahr', ev.target.value)} className={FELD} placeholder="z. B. < 50 l" />
        </label>
        <label className="block">
          <span className={LABEL}>SDB-Datum</span>
          <input type="date" value={e.sdbDatum ?? ''} onChange={(ev) => setze('sdbDatum', ev.target.value || null)} className={FELD} />
        </label>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <span className={LABEL}>Substitution geprüft am</span>
          <input type="date" value={e.substitutionGeprueftAm ?? ''} onChange={(ev) => setze('substitutionGeprueftAm', ev.target.value || null)} className={FELD} />
        </label>
        <label className="block">
          <span className={LABEL}>Ergebnis</span>
          <input value={e.substitutionErgebnis ?? ''} onChange={(ev) => setze('substitutionErgebnis', ev.target.value)} className={FELD} placeholder="z. B. kein Ersatz möglich" />
        </label>
      </div>
      <label className="block">
        <span className={LABEL}>Notiz</span>
        <input value={e.notiz ?? ''} onChange={(ev) => setze('notiz', ev.target.value)} className={FELD} />
      </label>
      <label className="flex items-center gap-2 text-sm text-slate-700">
        <input type="checkbox" checked={e.aktiv} onChange={(ev) => setze('aktiv', ev.target.checked)} /> aktiv
      </label>
    </Dialog>
  );
};
